import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Upload, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { blogApi, BLOG_IMPORT_EXAMPLE } from "@/lib/blogApi";

export default function AdminBlogImport() {
    const navigate = useNavigate();
    const [raw, setRaw] = useState("");
    const [parseError, setParseError] = useState("");
    const [result, setResult] = useState(null);
    const [importing, setImporting] = useState(false);

    const copyExample = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(BLOG_IMPORT_EXAMPLE, null, 2));
            toast.success("Example copied");
        } catch {
            toast.error("Copy failed — select and copy manually");
        }
    };

    const run = async () => {
        setParseError("");
        setResult(null);
        if (!raw.trim()) {
            setParseError("Paste a JSON object or array first.");
            return;
        }
        let payload;
        try {
            payload = JSON.parse(raw);
        } catch (err) {
            setParseError(`JSON parse error: ${err.message}`);
            return;
        }
        if (!Array.isArray(payload)) {
            if (typeof payload !== "object" || payload === null) {
                setParseError("Payload must be an object or an array of objects.");
                return;
            }
            payload = [payload];
        }
        setImporting(true);
        try {
            const r = await blogApi.importJson(payload);
            setResult(r);
            if (r.total_created > 0) {
                toast.success(`${r.total_created} draft(s) created`);
            }
            if (r.errors?.length) {
                toast.error(`${r.errors.length} row(s) rejected — see details below`);
            }
        } catch (err) {
            setParseError(err?.response?.data?.detail || err?.message || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-blog-import-page">
            <div>
                <Link
                    to="/admin/blog"
                    className="inline-flex items-center gap-1 text-xs text-mm-muted hover:text-mm-blue"
                    data-testid="import-back"
                >
                    <ArrowLeft size={12} /> Back to posts
                </Link>
                <h1 className="mt-1 font-serif text-3xl text-mm-navy">Import JSON</h1>
                <p className="mt-1 max-w-2xl text-sm text-mm-muted">
                    Paste a single post object or an array of posts. Each post lands as a <strong>draft</strong>.
                    Fields: <code>title, slug, meta_title, meta_description, category, tags, hero_image_url,
                    hero_image_alt, body_markdown, faq[&#123;q,a&#125;], disclaimer, publish_at</code>.
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
                <div className="lg:col-span-3 space-y-3 rounded-xl border border-mm-line bg-white p-5">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-mm-navy">Paste JSON</h3>
                        <button
                            onClick={copyExample}
                            className="inline-flex items-center gap-1 rounded-full border border-mm-line px-3 py-1 text-xs font-medium hover:bg-mm-bg"
                            data-testid="import-copy-example"
                        >
                            <Copy size={11} />
                            Copy example
                        </button>
                    </div>
                    <textarea
                        value={raw}
                        onChange={(e) => setRaw(e.target.value)}
                        className="h-96 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-mm-sky"
                        placeholder='{ "title": "...", "slug": "...", "meta_title": "...", "meta_description": "...", "category": "Life", "tags": [], "hero_image_url": "...", "hero_image_alt": "...", "body_markdown": "## Hello", "faq": [], "disclaimer": "" }'
                        data-testid="import-textarea"
                    />
                    {parseError && (
                        <div
                            className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                            data-testid="import-error"
                        >
                            <AlertCircle size={14} className="mt-0.5 shrink-0" />
                            <span>{parseError}</span>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <button
                            onClick={run}
                            disabled={importing}
                            className="mm-btn-primary !px-4 !py-2 text-sm"
                            data-testid="import-run"
                        >
                            {importing ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                            Import
                        </button>
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-xl border border-mm-line bg-white p-5">
                        <h3 className="mb-2 text-sm font-semibold text-mm-navy">Expected shape</h3>
                        <p className="mb-3 text-xs text-mm-muted">
                            All fields except <code>slug</code>, <code>tags</code>, <code>faq</code>,
                            <code> disclaimer</code>, and <code>publish_at</code> are required.
                        </p>
                        <pre
                            className="max-h-96 overflow-y-auto rounded-lg bg-mm-bg/60 p-3 text-[0.7rem] text-mm-ink"
                            data-testid="import-example"
                        >
{JSON.stringify(BLOG_IMPORT_EXAMPLE, null, 2)}
                        </pre>
                    </div>
                </div>
            </div>

            {result && (
                <div
                    className="space-y-4 rounded-xl border border-mm-line bg-white p-5"
                    data-testid="import-result"
                >
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-mm-navy">
                            Result: {result.total_created} of {result.total_in} imported
                        </h3>
                        {result.total_created > 0 && (
                            <button
                                onClick={() => navigate("/admin/blog")}
                                className="rounded-full border border-mm-line px-3 py-1 text-xs font-medium hover:bg-mm-bg"
                                data-testid="import-go-list"
                            >
                                Go to posts
                            </button>
                        )}
                    </div>
                    {result.created?.length > 0 && (
                        <ul className="space-y-1">
                            {result.created.map((p) => (
                                <li
                                    key={p.id}
                                    className="flex items-center gap-2 text-sm text-emerald-800"
                                    data-testid={`import-created-${p.slug}`}
                                >
                                    <CheckCircle2 size={13} />
                                    <span>
                                        <strong>{p.title}</strong>{" "}
                                        <span className="text-mm-muted">/blog/{p.slug}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {result.errors?.length > 0 && (
                        <div className="space-y-1 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
                            <div className="font-semibold text-rose-800">
                                {result.errors.length} row(s) rejected
                            </div>
                            {result.errors.map((e, i) => (
                                <div key={i} className="text-rose-800" data-testid={`import-error-row-${i}`}>
                                    <strong>Row {e.row}</strong>
                                    {e.title && <> — "{e.title}"</>}:
                                    <ul className="ml-4 list-disc">
                                        {(e.errors || []).map((msg, j) => (
                                            <li key={j}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
